import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import BasicModal from "../../../../../components/modals/BasicModal";
import { useColumnTypesLoanFilesTable } from "../../LoanDrilldownFilesTab/LoanFilesTable/hooks";

import { Box, Button } from "@mui/material";
import { amber, green, grey, orange, red } from "@mui/material/colors";
import { isEmpty, isNil, size } from "lodash";
import CellRendererLoanFileAssociations from "../../../../../components/agGrid/cellRenderer/CellRendererLoanFileAssociations";
import CellRendererLoanFileStatus from "../../../../../components/agGrid/cellRenderer/CellRendererLoanFileStatus";
import SearchInput from "../../../../../components/inputs/SearchInput";
import Flx from "../../../../../components/layout/Flx";
import { selectOptionsDocumentStatus } from "../../../../../constants/selectOptions/selectOptionsDocumentStatus";
import { useFileDocGroupAndTypesHook } from "../../../../../hooks/useFileDocGroupAndTypesHook";
import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";

const LoanBorrowerAssignFilesModal = ({
  borrowerId,
  propertyId,
  entityId,
  show,
  onClose,
}) => {
  const [selected, setSelected] = useState(null);
  const [quickFilter, setQuickFilter] = useState("");
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, associateDocumentWithObjects } = useLoanFilesHook();
  const dispatch = useDispatch();

  const handleOnClose = useCallback((props) => {
    setSelected(null);
    setQuickFilter("");
    if (onClose) {
      onClose();
    }
  }, []);

  const title = useMemo(() => {
    if (borrowerId) {
      return `Assign Files to Borrower`;
    }
    if (propertyId) {
      return `Assign Files to Property`;
    }
    if (entityId) {
      return `Assign Files to Entity`;
    }
  }, [borrowerId, propertyId, entityId]);

  const onSubmit = useCallback(() => {
    const selectedIds = selected.map((s) => s._id);
    associateDocumentWithObjects({
      dealId: loanDrilldown?._id,
      loanDocumentsIds: selectedIds,
      borrowerIds: [borrowerId],
      entityIds: [entityId],
      propertyIds: [propertyId],
      // payload: {
      //   borrowers: [borrower._id],
      // },
      onSuccessFn: (d) => {
        dispatch(loanDrilldownSet(d));
        handleOnClose();
      },
    });
  }, [loanDrilldown?._id, selected, associateDocumentWithObjects]);
  if (!show) {
    return null;
  }
  return (
    <BasicModal
      description={"Select the files you would like to assign"}
      suppressBodyPadding
      title={title}
      maxWidth="lg"
      onClose={handleOnClose}
      show={show}
    >
      <Box sx={{ p: 1 }}>
        <SearchInput
          fullWidth
          value={quickFilter}
          onChange={(e) => setQuickFilter(e)}
        />
      </Box>
      <LoanFilesSelectableTable
        quickFilter={quickFilter}
        onSelectionChanged={setSelected}
      />

      <Flx
        fw
        end
        gap={1}
        sx={{
          p: 2,
          background: grey[100],
          borderTop: `1px solid ${grey[300]}`,
        }}
      >
        <Button
          size="large"
          color="error"
          variant={"outlined"}
          onClick={handleOnClose}
        >
          Cancel
        </Button>

        <Button
          loading={loading}
          disabled={isNil(selected) || isEmpty(selected)}
          // variant={primaryButtonVariant}
          size="large"
          // color="success"
          loadingPosition="end"
          onClick={onSubmit}
        >
          Save & Assign ({size(selected)})
        </Button>
      </Flx>
    </BasicModal>
  );
};

const LoanFilesSelectableTable = ({ quickFilter, onSelectionChanged }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loanDrilldown?.loanDocuments;
  const { fileDocGroups, getDocTypesByGroup } = useFileDocGroupAndTypesHook();
  // const borrowerAssociations = useMemo(
  //   () => borrower?.associations,
  //   [borrower?.associations]
  // );
  // const borrowerAssociationIds = borrowerAssociations?.loanDocuments;

  // const borrowerFiles = useMemo(() => {
  //   return loanDocuments?.filter((doc) =>
  //     borrowerAssociationIds?.includes(doc._id)
  //   );
  // }, [loanDocuments, borrowerAssociationIds]);

  const rowData = useMemo(() => loanDocuments, [loanDocuments]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Filename",
        field: "file_display_name",
        minWidth: 250,
      },

      {
        headerName: "Category",
        field: "docGroup",
        showRowGroup: "docGroup",
        sort: "asc",
        minWidth: 220,
        flex: 0,
        valueGetter: (params) => {
          const value = params.data?.docGroup;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        cellEditorParams: {
          values: fileDocGroups || [],
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          valueListMaxHeight: 220,
          valueListWidth: 620,
        },
      },
      {
        headerName: "Document Type",
        field: "docType",
        // sort: "asc",
        sort: "asc",

        showRowGroup: "docType",

        minWidth: 240,
        flex: 0,
        cellEditorParams: (params) => {
          return {
            values: getDocTypesByGroup(params.data.docGroup),
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
            valueListMaxHeight: 220,
            valueListWidth: 620,
          };
        },
      },

      {
        headerName: "Associations",
        cellRenderer: CellRendererLoanFileAssociations,
      },
      {
        headerName: "Status",
        field: "documentStatus",
        valueGetter: (params) => {
          const value = params.data?.documentStatus;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        minWidth: 160,
        width: 160,
        flex: 0,
        // Custom comparator for sorting in specific order
        comparator: (valueA, valueB) => {
          const statusOrder = [
            "New Upload",
            "Pending Review",
            "Incomplete",
            "Rejected",
            "Accepted",
            // "Initial Reviewed",
            // "Legal Reviewed",
            // "Rejected",
            // "Expired",
            // "Uncategorized", // Put uncategorized at the end
          ];
          const indexA = statusOrder.indexOf(valueA);
          const indexB = statusOrder.indexOf(valueB);
          // Handle values not in the predefined order (put them at the end)
          const finalIndexA = indexA === -1 ? statusOrder.length : indexA;
          const finalIndexB = indexB === -1 ? statusOrder.length : indexB;
          return finalIndexA - finalIndexB;
        },
        cellEditorParams: {
          values: selectOptionsDocumentStatus || [],
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          valueListMaxHeight: 220,
          valueListWidth: 620,
        },
        cellClass: "ag-cell-vertical-center",
        cellStyle: (params) => {
          const status = params?.data?.documentStatus;
          const baseStyles = {
            color: "inherit",
            background: null,
            borderLeft: null,
          };

          if (
            status === "Initial Reviewed" ||
            status === "Legal Reviewed" ||
            status === "Pending Review"
          ) {
            baseStyles.background = amber[50];
            baseStyles.borderLeft = `2px solid ${orange[100]}`;
          }
          if (
            status === "Expired" ||
            status === "Rejected" ||
            status === "Incomplete"
          ) {
            baseStyles.background = red[50];
            baseStyles.color = red[900];
            baseStyles.borderLeft = `2px solid ${red[100]}`;
          }

          if (status === "Accepted") {
            baseStyles.color = green[900];
            baseStyles.background = green[50];
            baseStyles.borderLeft = `2px solid ${green[100]}`;
          }
          return baseStyles;
        },

        autoHeight: true,
        cellRenderer: (params) => (
          <CellRendererLoanFileStatus params={params} />
        ),
      },
    ],
    [fileDocGroups, getDocTypesByGroup],
  );
  return (
    <>
      <AgSelectableTable
        // height="500px"
        height="900px"
        rowData={rowData}
        columnDefs={columnDefs}
        quickFilter={quickFilter}
        onSelectionChanged={onSelectionChanged}
        sx={{ maxHeight: "60vh" }}
      />
    </>
  );
};

const AgSelectableTable = forwardRef(
  (
    {
      rowData,
      columnDefs,
      columnTypes,
      quickFilter,
      onRowClicked,
      onCellClicked,
      onSelectionChanged,
      height = "100%",
      autoHeight = false,
      sx,
    },
    ref,
  ) => {
    const handleRowSelectionChange = useCallback((params) => {
      const selected = params.api.getSelectedRows();
      onSelectionChanged(selected);
    }, []);

    const defaultColumnTypes = useColumnTypesLoanFilesTable();

    const sharedColumnTypes = useMemo(() => {
      return {
        ...defaultColumnTypes,
        ...columnTypes,
      };
    }, [defaultColumnTypes, columnTypes]);

    const domLayout = useMemo(
      (props) => {
        return autoHeight ? "autoHeight" : "normal";
      },
      [autoHeight],
    );
    return (
      <AgGridTableWrapper
        // height="500px"
        // height="100%"
        suppressMinHeightWhenPopulated={rowData}
        simpleTable
        size={4}
        sx={{
          // maxHeight: "80vh",
          width: "100%",
          height: height,

          ...sx,
          //
        }}
      >
        <AgGridReact
          ref={ref}
          rowData={rowData}
          columnDefs={columnDefs}
          columnTypes={sharedColumnTypes}
          singleClickEdit
          quickFilterText={quickFilter}
          stopEditingWhenCellsLoseFocus
          onRowClicked={onRowClicked}
          onCellClicked={onCellClicked}
          // rowHeight={50}
          defaultColDef={{
            flex: 1,
            minWidth: 150,
          }}
          onSelectionChanged={handleRowSelectionChange}
          rowSelection={{ mode: "multiRow" }}
          //  selectionColumnDef={selectionColumnDef}

          domLayout={domLayout}
        />
      </AgGridTableWrapper>
    );
  },
);

export default LoanBorrowerAssignFilesModal;
